import { useEffect, useMemo, useState } from "react"
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  Copy,
  Eye,
  EyeOff,
  GripVertical,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  X,
} from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { ProductCombobox } from "@/components/ProductCombobox"
import { getRoleMeta, roleOptionsForKind } from "@/components/RoleIcons"
import type { Kind } from "@/lib/kind-labels"
import { formatEur } from "@/lib/format"
import { effectiveCost, publicName, MODULE_NO_MARGIN_ROLES, SOLAR_NO_MARGIN_ROLES } from "@/lib/api"
import { cn } from "@/lib/utils"
import { confirmDialog } from "@/lib/confirm"

export interface KitLineDraft {
  uid: string
  type: "product" | "manual"
  product_id: number | null
  name: string
  description?: string
  qty: number
  unit_price: number
  total: number
  role?: string
  position: number
  /** Escondida na apresentação do PDF (mantém-se no cálculo do preço). */
  hidden_in_pdf?: boolean
}

interface KitEditorProps {
  shell: AppShell | null
  simulationId: string
  brandSolarSlug: string | null
  /** Bateria incluída (backup implica bateria) — controla a label da margem. */
  hasBattery: boolean
  appliedMargin: number
  vatMultiplier: number
  taxExemptionFactor: number
  maxDiscountFraction: number
  /**
   * Desconto automático por baterias (solar): com `minQty`+ baterias, `fraction`
   * sobre o valor final, somado ao ajuste manual — o mesmo que o servidor
   * aplica ao gravar (functions/lib/engine/battery-discount.ts). Só propostas
   * elegíveis (as novas; nunca os kits do site).
   */
  batteryDiscount?: { eligible: boolean; minQty: number; fraction: number }
  originalLines: KitLineDraft[]
  originalDiscountFraction: number
  originalTotalPriceEur: number
  /** Estado actual do flag "esconder retorno do investimento / payback". */
  originalHidePayback: boolean
  onCancel: () => void
  onSaved: () => void
  /**
   * Módulo. Solar (default) → roles solar + actions/solar_update_kit +
   * product picker por category_roles. AC/BC → roles do módulo +
   * actions/update_kit (genérico) + picker por metadata.
   */
  kind?: Kind
  /** AC/BC: marca da proposta — o picker de produtos só mostra essa marca. */
  moduleBrand?: string | null
}

const uid = () => Math.random().toString(36).slice(2, 10)

export function KitEditor({
  shell,
  simulationId,
  brandSolarSlug,
  hasBattery,
  appliedMargin,
  vatMultiplier,
  taxExemptionFactor,
  maxDiscountFraction,
  batteryDiscount,
  originalLines,
  originalDiscountFraction,
  originalTotalPriceEur,
  originalHidePayback,
  onCancel,
  onSaved,
  kind = "solar",
  moduleBrand = null,
}: KitEditorProps) {
  const actionVerb = kind === "solar" ? "actions/solar_update_kit" : "actions/update_kit"
  const roleOptions = roleOptionsForKind(kind)
  const [lines, setLines] = useState<KitLineDraft[]>(originalLines)
  // Ajuste de preço com sinal (guardado como discount_fraction): desconto > 0,
  // aumento < 0. A UI separa o modo (Desconto/Aumento) da percentagem.
  const [adjustMode, setAdjustMode] = useState<"desconto" | "aumento">(
    originalDiscountFraction < 0 ? "aumento" : "desconto",
  )
  const [adjustPct, setAdjustPct] = useState<number>(
    Math.abs(originalDiscountFraction) * 100,
  )
  /** Valor final pretendido (c/ IVA) — atalho que calcula a percentagem. */
  const [targetFinal, setTargetFinal] = useState<string>("")
  const [hidePayback, setHidePayback] = useState<boolean>(originalHidePayback)
  const [saving, setSaving] = useState(false)

  // Desconto: admin OU flag `allow_seller_discount` (resolvido em /api/me →
  // shell.canDiscount). O AUMENTO está sempre disponível para todos. Quem não
  // pode dar desconto nunca fica em modo "desconto" (ex.: abriu um kit que já
  // tinha desconto) — força "aumento".
  const canDiscount = shell?.canDiscount === true
  useEffect(() => {
    if (!canDiscount && adjustMode === "desconto") setAdjustMode("aumento")
  }, [canDiscount, adjustMode])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function patchLine(uidKey: string, p: Partial<KitLineDraft>) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.uid !== uidKey) return l
        const merged = { ...l, ...p }
        merged.total = round(merged.qty * merged.unit_price, 2)
        return merged
      }),
    )
  }

  function removeLine(uidKey: string) {
    setLines((prev) =>
      prev
        .filter((l) => l.uid !== uidKey)
        .map((l, i) => ({ ...l, position: i })),
    )
  }

  function duplicateLine(uidKey: string) {
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.uid === uidKey)
      if (idx < 0) return prev
      const copy: KitLineDraft = { ...prev[idx], uid: uid() }
      const next = [...prev]
      next.splice(idx + 1, 0, copy)
      return next.map((l, i) => ({ ...l, position: i }))
    })
  }

  function addLine(type: "product" | "manual") {
    setLines((prev) => [
      ...prev,
      {
        uid: uid(),
        type,
        product_id: null,
        name: type === "manual" ? "" : "",
        qty: 1,
        unit_price: 0,
        total: 0,
        role: type === "manual" ? undefined : "accessory",
        position: prev.length,
      },
    ])
  }

  async function clearAll() {
    if (!(await confirmDialog("Remover todas as linhas?", { confirmLabel: "Remover", destructive: true }))) return
    setLines([])
  }

  async function restoreOriginal() {
    if (!(await confirmDialog("Descartar alterações e restaurar valores originais?", { confirmLabel: "Restaurar" })))
      return
    setLines(originalLines)
    setAdjustMode(originalDiscountFraction < 0 ? "aumento" : "desconto")
    setAdjustPct(Math.abs(originalDiscountFraction) * 100)
    setHidePayback(originalHidePayback)
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    setLines((prev) => {
      const oldIndex = prev.findIndex((l) => l.uid === active.id)
      const newIndex = prev.findIndex((l) => l.uid === over.id)
      const moved = arrayMove(prev, oldIndex, newIndex)
      return moved.map((l, i) => ({ ...l, position: i }))
    })
  }

  // ── Live preview totals ─────────────────────────────────────────────
  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + l.total, 0),
    [lines],
  )
  // Separa o custo em bolsa COM margem (equipamentos) e SEM margem (instalação/
  // mão de obra — solar e AC/BC, cada um com o seu conjunto de roles). Espelha
  // computePrice / applyMarginByLines: a margem só incide na bolsa com-margem;
  // a sem-margem entra a custo. Sem isto o preview não bate com o gravado.
  const noMarginRoles = kind === "solar" ? SOLAR_NO_MARGIN_ROLES : MODULE_NO_MARGIN_ROLES
  const { costWithMargin, costNoMargin } = useMemo(() => {
    let cwm = 0
    let cnm = 0
    for (const l of lines) {
      if (l.role && noMarginRoles.has(l.role)) {
        cnm += l.total
      } else {
        cwm += l.total
      }
    }
    return { costWithMargin: cwm, costNoMargin: cnm }
  }, [lines, noMarginRoles])
  // Fracção com sinal enviada ao backend. Desconto é capado por
  // maxDiscountFraction (0 = sem cap); aumento não é capado (magnitude ≤ 100%).
  const pctAbs = Math.max(0, Math.min(100, adjustPct)) / 100
  // Espelha computePrice (solar) / applyMarginByLines (AC/BC): a MARGEM só na
  // bolsa com-margem; o AJUSTE incide no VALOR FINAL (pedido do cliente: "5% em
  // cima do valor final", igual sem IVA e com IVA).
  const effectiveVat = 1 + (vatMultiplier - 1) * taxExemptionFactor
  const baseNoVat = costWithMargin * appliedMargin + costNoMargin
  /** Valor final (c/ IVA) sem qualquer ajuste — base do "valor pretendido". */
  const baseFinalWithVat = baseNoVat * effectiveVat
  /**
   * Com "valor final pretendido" preenchido, a fracção vem do valor EXACTO —
   * não da percentagem arredondada (2 casas dariam erro de cêntimos: pedir
   * 11 760 € saía 11 760,37 €). Recalcula-se sempre que as linhas, a margem ou
   * o IVA mudam, por isso o valor final mantém-se no alvo.
   */
  // Desconto automático por baterias (espelha battery-discount.ts no servidor).
  const batteries = lines.reduce((n, l) => (l.role === "battery" ? n + (Number(l.qty) || 0) : n), 0)
  const autoDiscount =
    batteryDiscount?.eligible && batteryDiscount.fraction > 0 && batteries >= batteryDiscount.minQty ? batteryDiscount.fraction : 0
  const targetValue = Number(targetFinal)
  // O valor pretendido já conta com o desconto automático: o ajuste manual é o que falta.
  const exactFraction =
    targetFinal.trim() !== "" && Number.isFinite(targetValue) && targetValue > 0 && baseFinalWithVat > 0
      ? 1 - targetValue / baseFinalWithVat - autoDiscount
      : null
  const rawFraction = exactFraction ?? (adjustMode === "desconto" ? pctAbs : -pctAbs)
  const clamped = Math.max(-1, Math.min(rawFraction, 1))
  const discountCapped = clamped > 0 && maxDiscountFraction > 0 && clamped > maxDiscountFraction
  // Sem permissão para descontar, um alvo abaixo do preço de tabela não é
  // aplicado (o backend também o apararia para 0).
  const blockedDiscount = clamped > 0 && !canDiscount
  const signedFraction = blockedDiscount
    ? 0
    : clamped > 0 && maxDiscountFraction > 0
      ? Math.min(clamped, maxDiscountFraction)
      : clamped
  const priceNoVat = baseNoVat * (1 - Math.min(signedFraction + autoDiscount, 1))
  const priceWithVat = priceNoVat * effectiveVat
  const diffEur = priceWithVat - originalTotalPriceEur
  const diffPct =
    originalTotalPriceEur > 0
      ? (diffEur / originalTotalPriceEur) * 100
      : 0

  // Com "valor final" preenchido, o modo e a percentagem são só leitura: saem
  // da fracção exacta. Mexer na percentagem ou no modo larga o alvo.
  const effectiveMode: "desconto" | "aumento" =
    exactFraction != null ? (exactFraction >= 0 ? "desconto" : "aumento") : adjustMode
  const displayPct =
    exactFraction != null ? Math.round(Math.abs(signedFraction) * 10000) / 100 : adjustPct
  function clearTarget() {
    if (targetFinal !== "") setTargetFinal("")
  }

  async function save() {
    if (!shell) return
    setSaving(true)
    try {
      const payload = {
        id: simulationId,
        lines: lines.map((l, i) => ({
          type: l.type,
          product_id: l.product_id,
          name: l.name,
          description: l.description,
          qty: l.qty,
          unit_price: l.unit_price,
          total: l.total,
          role: l.role,
          position: i,
          hidden_in_pdf: l.hidden_in_pdf ?? false,
        })),
        discount_fraction: signedFraction,
        hide_payback: hidePayback,
      }
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        actionVerb,
        { params: payload },
      )
      if (!res.success) throw new Error(res.error)
      shell.bridge.toast({
        variant: "success",
        description: "Kit actualizado",
      })
      onSaved()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-accent/30 bg-accent/5 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-accent">
              Modo edição do kit
            </h3>
            <p className="text-xs text-muted-foreground">
              Arrasta para reordenar · Edita inline · Vê o preço a actualizar em
              tempo real · Guarda no fim
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={restoreOriginal}>
              <RotateCcw className="h-4 w-4" />
              Restaurar
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll}>
              <Trash2 className="h-4 w-4" />
              Limpar tudo
            </Button>
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={lines.map((l) => l.uid)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {lines.map((line) => (
              <SortableLine
                key={line.uid}
                line={line}
                shell={shell}
                brandSolarSlug={brandSolarSlug}
                moduleBrand={moduleBrand}
                kind={kind}
                roleOptions={roleOptions}
                onPatch={(p) => patchLine(line.uid, p)}
                onRemove={() => removeLine(line.uid)}
                onDuplicate={() => duplicateLine(line.uid)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => addLine("product")}>
          <Plus className="h-4 w-4" />
          Adicionar componente
        </Button>
        <Button variant="ghost" size="sm" onClick={() => addLine("manual")}>
          <Plus className="h-4 w-4" />
          Linha manual
        </Button>
      </div>

      {/* Ajuste de preço. O AUMENTO está sempre disponível (todos os Cargos). O
          DESCONTO só aparece quando o utilizador pode descontar (Administrador/
          Master, ou o flag "Permitir desconto a vendedores" ligado nas
          Configurações — via shell.canDiscount resolvido por /api/me). O backend
          também apara descontos não autorizados para 0. */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Label>{canDiscount ? "Ajuste de preço" : "Aumento de preço"}</Label>
          <div className="flex items-center gap-2">
            {/* Toggle Desconto / Aumento (Desconto só quando permitido) */}
            <div className="inline-flex overflow-hidden rounded-md border border-border">
              {canDiscount && (
              <button
                type="button"
                onClick={() => {
                  clearTarget()
                  setAdjustMode("desconto")
                }}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors",
                  effectiveMode === "desconto"
                    ? "bg-emerald-600 text-white"
                    : "bg-transparent text-muted-foreground hover:bg-muted",
                )}
              >
                Desconto
              </button>
              )}
              <button
                type="button"
                onClick={() => {
                  clearTarget()
                  setAdjustMode("aumento")
                }}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors",
                  effectiveMode === "aumento"
                    ? "bg-amber-600 text-white"
                    : "bg-transparent text-muted-foreground hover:bg-muted",
                )}
              >
                Aumento
              </button>
            </div>
            {/* Campo de percentagem */}
            <div className="relative w-24">
              <Input
                type="number"
                step="0.5"
                min={0}
                max={100}
                value={displayPct}
                onChange={(e) => {
                  clearTarget()
                  setAdjustPct(Math.max(0, Number(e.target.value) || 0))
                }}
                className="h-8 pr-6 text-right"
              />
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                %
              </span>
            </div>
            {/* Atalho: escrever o valor final pretendido (c/ IVA) e deixar a
                percentagem ser calculada — é assim que o comercial trabalha
                ("preciso de fechar em 11 760 €"). */}
            <div className="relative w-32">
              <Input
                type="number"
                step="0.01"
                min={0}
                placeholder="Valor final"
                value={targetFinal}
                onChange={(e) => setTargetFinal(e.target.value)}
                className="h-8 pr-6 text-right"
              />
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                €
              </span>
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {effectiveMode === "desconto" ? "Diminui" : "Aumenta"} o preço na
          percentagem indicada, sobre o <b>valor final</b> — a mesma
          percentagem sem IVA e com IVA.
          {" "}Ou escreve o <b>valor final</b> pretendido (com IVA) e a
          percentagem é calculada a partir dele, ao cêntimo.
          {maxDiscountFraction > 0 && effectiveMode === "desconto" && (
            <> Limite de desconto: {(maxDiscountFraction * 100).toFixed(0)}%.</>
          )}
        </p>
        {blockedDiscount && (
          <p className="mt-1 text-xs font-medium text-amber-600">
            Esse valor fica abaixo do preço de tabela e não tens permissão para
            descontar — nada foi aplicado.
          </p>
        )}
        {autoDiscount > 0 && (
          <p className="mt-1 text-xs font-medium text-emerald-700">
            Desconto automático de {(autoDiscount * 100).toFixed(0)}% por {batteries} baterias (regra das Configurações) — soma ao
            ajuste acima e aparece no PDF.
          </p>
        )}
        {batteryDiscount?.eligible && autoDiscount === 0 && batteryDiscount.fraction > 0 && batteries > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            Com {batteryDiscount.minQty} ou mais baterias entra um desconto automático de {(batteryDiscount.fraction * 100).toFixed(0)}%.
          </p>
        )}
        {discountCapped && (
          <p className="mt-1 text-xs font-medium text-amber-600">
            Desconto limitado a {(maxDiscountFraction * 100).toFixed(0)}% (aplica-se{" "}
            {(signedFraction * 100).toFixed(1)}%).
          </p>
        )}
      </div>

      {/* Esconder retorno do investimento / payback nos PDF */}
      <div className="flex items-center justify-between rounded-md border border-border bg-card p-4">
        <div className="pr-4">
          <Label htmlFor="hide-payback">Esconder retorno do investimento</Label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Quando activo, o gráfico de retorno do investimento e o payback não
            aparecem em nenhum PDF desta proposta.
          </p>
        </div>
        <Switch
          id="hide-payback"
          checked={hidePayback}
          onCheckedChange={setHidePayback}
        />
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-10 -mx-2 rounded-md border border-border bg-card/95 px-4 py-3 shadow-lg backdrop-blur sm:mx-0">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
            <PriceMini label="Subtotal (custo)" value={formatEur(subtotal)} />
            <PriceMini
              label={kind === "solar" ? `Margem ${hasBattery ? "c/ bateria" : "s/ bateria"}` : "Margem"}
              value={`× ${appliedMargin.toFixed(4)}`}
              muted
            />
            <PriceMini
              label="IVA efectivo"
              value={`× ${effectiveVat.toFixed(4)}`}
              muted
            />
            <PriceMini
              label="Preço final"
              value={formatEur(priceWithVat)}
              big
              diff={diffEur !== 0 ? { eur: diffEur, pct: diffPct } : undefined}
            />
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" onClick={onCancel} disabled={saving}>
              <X className="h-4 w-4" />
              Cancelar
            </Button>
            <Button onClick={save} disabled={saving || lines.length === 0}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? "A guardar…" : "Guardar alterações"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function PriceMini({
  label,
  value,
  muted,
  big,
  diff,
}: {
  label: string
  value: string
  muted?: boolean
  big?: boolean
  diff?: { eur: number; pct: number }
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          big ? "text-lg font-semibold" : "text-sm font-medium",
          muted && "text-muted-foreground",
        )}
      >
        {value}
        {diff && (
          <span
            className={cn(
              "ml-2 text-xs font-medium",
              diff.eur > 0 ? "text-amber-600" : "text-emerald-600",
            )}
          >
            {diff.eur > 0 ? "+" : ""}
            {formatEur(diff.eur)} ({diff.eur > 0 ? "+" : ""}
            {diff.pct.toFixed(1)}%)
          </span>
        )}
      </span>
    </div>
  )
}

// ============================================================
// Sortable Line
// ============================================================

function SortableLine({
  line,
  shell,
  brandSolarSlug,
  moduleBrand,
  kind,
  roleOptions,
  onPatch,
  onRemove,
  onDuplicate,
}: {
  line: KitLineDraft
  shell: AppShell | null
  brandSolarSlug: string | null
  moduleBrand: string | null
  kind: Kind
  roleOptions: Array<{ value: string; label: string }>
  onPatch: (p: Partial<KitLineDraft>) => void
  onRemove: () => void
  onDuplicate: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: line.uid })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const roleMeta = getRoleMeta(line.role)
  const RoleIcon = roleMeta.Icon

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group rounded-md border border-border bg-card transition-shadow",
        isDragging && "shadow-lg ring-2 ring-accent/40",
      )}
    >
      <div className="flex items-stretch">
        <button
          type="button"
          aria-label="Arrastar para reordenar"
          {...attributes}
          {...listeners}
          className="flex w-8 cursor-grab items-center justify-center rounded-l-md border-r border-border bg-muted/40 hover:bg-muted active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>

        <div className="flex-1 space-y-3 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={cn(
                "gap-1 border-border",
                line.type === "manual" && "text-muted-foreground",
              )}
            >
              <RoleIcon className={cn("h-3 w-3", roleMeta.color)} />
              {roleMeta.label}
            </Badge>

            {line.hidden_in_pdf && (
              <Badge
                variant="outline"
                className="gap-1 border-amber-300 text-amber-700 dark:text-amber-300"
              >
                <EyeOff className="h-3 w-3" />
                Oculto no PDF
              </Badge>
            )}

            <Select
              value={line.type}
              onValueChange={(v) =>
                onPatch({
                  type: v as "product" | "manual",
                  product_id: v === "manual" ? null : line.product_id,
                })
              }
            >
              <SelectTrigger className="h-7 w-32 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="product">Componente</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={line.role ?? ""}
              onValueChange={(v) => onPatch({ role: v })}
            >
              <SelectTrigger className="h-7 w-40 text-xs">
                <SelectValue placeholder="Categoria…" />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div
              className={cn(
                "ml-auto flex gap-1 transition-opacity",
                line.hidden_in_pdf
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100",
              )}
            >
              <button
                type="button"
                onClick={() => onPatch({ hidden_in_pdf: !line.hidden_in_pdf })}
                className={cn(
                  "rounded p-1 hover:bg-muted",
                  line.hidden_in_pdf
                    ? "text-amber-600"
                    : "text-muted-foreground hover:text-foreground",
                )}
                title={line.hidden_in_pdf ? "Mostrar no PDF" : "Esconder no PDF"}
              >
                {line.hidden_in_pdf ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={onDuplicate}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                title="Duplicar"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="rounded p-1 text-destructive hover:bg-destructive/10"
                title="Remover"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {line.type === "product" ? (
            <div>
              <Label className="text-xs">Produto</Label>
              {line.role ? (
                <ProductCombobox
                  shell={shell}
                  role={line.role}
                  brandSlug={brandSolarSlug}
                  kind={kind}
                  moduleBrand={moduleBrand}
                  value={line.product_id}
                  valueName={line.name}
                  onChange={(p) =>
                    onPatch({
                      product_id: p?.id ?? null,
                      name: p ? publicName(p) : "",
                      description: p?.description ?? undefined,
                      unit_price: p ? effectiveCost(p) : line.unit_price,
                    })
                  }
                />
              ) : (
                <div className="rounded-md border border-dashed border-border p-2 text-xs text-muted-foreground">
                  Selecciona uma categoria primeiro
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label className="text-xs">Descrição</Label>
              <Input
                value={line.name}
                onChange={(e) => onPatch({ name: e.target.value })}
                placeholder="Ex.: Transporte adicional"
              />
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Quantidade</Label>
              <Input
                type="number"
                step="0.01"
                value={line.qty}
                onChange={(e) =>
                  onPatch({ qty: Number(e.target.value) || 0 })
                }
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">
                Preço unitário (€)
                {line.type === "product" && (
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    (base do produto)
                  </span>
                )}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={line.unit_price}
                onChange={(e) =>
                  onPatch({ unit_price: Number(e.target.value) || 0 })
                }
                disabled={line.type === "product"}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Total (€)</Label>
              <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm font-medium">
                {formatEur(line.total)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function round(n: number, decimals = 0): number {
  const f = 10 ** decimals
  return Math.round(n * f) / f
}
